import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Divider,
  Drawer,
  Stack,
  Typography,
  useMediaQuery,
  Collapse,
} from "@mui/material";
import {
  items,
  adminItems,
  notaryItems,
  getPrivateProviderItems,
} from "./side-nav-items";
import { SideNavItem } from "./side-nav-item";
import SideNavAd from "./side-nav-ad";
import { useTour } from "@reactour/tour";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { isSubAdminEmail } from "../../functions/helperFunctions";

const SideNav = (props) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession]?.user);
  const { subAdmins } = useSelector((state) => state.nocData || {});
  const { subAdminDomains = [], subAdminPages = [] } = subAdmins || {};

  const { pathname } = useLocation();
  const lgUp = useMediaQuery((theme) => theme.breakpoints.up("lg"));

  const { isOpen, currentStep } = useTour();
  const { open, onClose } = props;

  const [openItems, setOpenItems] = useState({});

  const scrollContainerRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const activeItemRef = useRef(null);

  const scrollbarStyles = {
    overflowY: "auto",
    flexGrow: 1,
    px: 2,
    py: 3,
    msOverflowStyle: "none", // For IE and Edge
    scrollbarWidth: "thin", // For Firefox

    /* Adding WebKit Scrollbar styling using a CSS string */
    "&::-webkit-scrollbar": {
      width: "12px",
    },
    "&::-webkit-scrollbar-track": {
      background: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#555",
      borderRadius: "10px",
      border: "3px solid #f1f1f1",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#333",
    },
  };

  useEffect(() => {
    // Automatically scroll to the active item when component mounts
    if (activeItemRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const activeEl = activeItemRef.current;
      const containerRect = container.getBoundingClientRect();
      const activeRect = activeEl.getBoundingClientRect();

      const isFullyVisible =
        activeRect.top >= containerRect.top &&
        activeRect.bottom <= containerRect.bottom;

      // Only adjust scroll when active item is out of view.
      // Use non-animated scrolling to avoid visible "flash" during route changes.
      if (!isFullyVisible) {
        activeEl.scrollIntoView({
          behavior: "auto",
          block: "nearest",
        });
      }
    }
  }, [pathname]);

  const handleScrollDown = () => {
    if (scrollContainerRef.current) {
      const { scrollHeight, clientHeight } = scrollContainerRef.current;
      const isAtBottom =
        scrollContainerRef.current.scrollTop + clientHeight >= scrollHeight;

      // Scroll down only if not at the bottom
      if (!isAtBottom) {
        scrollContainerRef.current.scrollTop += 15;
      }
    }
  };

  const handleScrollUp = () => {
    if (scrollContainerRef.current) {
      const isAtTop = scrollContainerRef.current.scrollTop === 0;

      // Scroll up only if not at the top
      if (!isAtTop) {
        scrollContainerRef.current.scrollTop -= 15;
      }
    }
  };

  const handleMouseUp = () => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  };

  const handleMouseDown = () => {
    if (!scrollIntervalRef.current) {
      handleScrollDown();
      scrollIntervalRef.current = setInterval(handleScrollDown, 100);
    }
  };

  const handleMouseDownUp = () => {
    if (!scrollIntervalRef.current) {
      handleScrollUp();
      scrollIntervalRef.current = setInterval(handleScrollUp, 100);
    }
  };

  const isSubAdmin = isSubAdminEmail(currentUser?.user?.email, subAdminDomains);

  const subAdminFilteredAdminItems = adminItems
    .map((item) => {
      if (!item.children) return item;

      return {
        ...item,
        children: item.children.filter((child) =>
          subAdminPages?.includes(child.path),
        ),
      };
    })
    .filter((item) => !item.children || item.children.length > 0);

  useEffect(() => {
    const scrollableElement = scrollContainerRef.current;
    const lastScrollPos = localStorage.getItem("sideNavScrollPos") || 0;

    if (scrollableElement) {
      scrollableElement.scrollTop = lastScrollPos;

      const handleScroll = () => {
        localStorage.setItem("sideNavScrollPos", scrollableElement.scrollTop);
      };

      scrollableElement.addEventListener("scroll", handleScroll);

      return () => {
        scrollableElement.removeEventListener("scroll", handleScroll);
      };
    }
  }, []);

  const handleToggle = (title) => {
    setOpenItems((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  //! BACKWARD NAVIGATION FOR USER
  useEffect(() => {
    if (isOpen) {
      const userNumbOne = 7;
      const userNumbTwo = 8;
      const subUserNumbOne = 5;
      const subUserNumbTwo = 6;

      const numberOne =
        currentSession === "subUserSession" ? subUserNumbOne : userNumbOne;
      const numberTwo =
        currentSession === "subUserSession" ? subUserNumbTwo : userNumbTwo;

      const title = "Permits Hub";

      if (currentStep === numberOne && openItems[title] === true) {
        handleToggle(title);
      } else if (currentStep === numberTwo && openItems[title] === false) {
        handleToggle(title);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);
  //! ////////////////////////////////////////////////////////////

  const isActive = (item) => {
    if (item.path) {
      // Check for exact match first
      if (pathname === item.path) return true;

      // Check for dynamic route match (routes with parameters like :id)
      if (item.path.includes(":")) {
        // Special case: Don't match "new" routes to dynamic routes
        if (pathname.endsWith("/new")) {
          return false;
        }

        // Convert route path to regex pattern
        const routePattern = item.path.replace(/:[^/]+/g, "[^/]+");
        const regex = new RegExp(`^${routePattern}$`);

        if (regex.test(pathname)) {
          return true;
        }
      }
    }

    // Check children for matches
    if (item.children && item.children.length > 0) {
      return item.children.some((child) => isActive(child));
    }

    // Special case: Match dynamic routes to their parent categories
    // This handles cases where dynamic routes don't have explicit nav items
    if (!item.path && item.children) {
      // Check if any child matches the current path (including dynamic routes)
      return item.children.some((child) => {
        if (child.path) {
          // Exact match
          if (pathname === child.path) return true;

          // Dynamic route match
          if (child.path.includes(":")) {
            // Special case: Don't match "new" routes to dynamic routes
            if (pathname.endsWith("/new")) {
              return false;
            }

            const routePattern = child.path.replace(/:[^/]+/g, "[^/]+");
            const regex = new RegExp(`^${routePattern}$`);
            return regex.test(pathname);
          }

          // Check if current path starts with child path (for sub-routes)
          if (pathname.startsWith(child.path + "/")) return true;
        }
        return false;
      });
    }

    return false;
  };

  const isActiveDemoItem = (item) => {
    if (isOpen) {
      if (currentStep === 0 && item.path === "/selectcounties") return true;
      if (currentStep === 2 && item.path === "/defaults") return true;
      if (currentStep === 5 && item.path === "/contractors") return true;
      if (currentStep === 7 && item.title === "Permits Hub") return true;
      if (currentStep === 8 && item.path === "/permits/create") return true;
      if (currentStep === 21 && item.path === "/subusers") return true;
      if (currentStep === 23 && item.path === "/subscription") return true;
      if (currentStep === 25 && item.path === "/payment") return true;
    }
    return false;
  };

  const renderNavItems = (navItems) => (
    <Stack
      component="ul"
      spacing={0.5}
      sx={{
        listStyle: "none",
        p: 0,
        m: 0,
      }}
    >
      {navItems.map((item) => {
        const active = isActive(item);
        const itemRef = active || isActiveDemoItem ? activeItemRef : null;

        // const isChildActive =
        //   item.children &&
        //   item.children.some((child) => pathname === child.path);

        let newTitle = null;
        if (
          item.title === "Manage Subscription" &&
          currentUser?.subuser?.role === "Power User"
        ) {
          newTitle = "Subscription Details";
        } else {
          newTitle = null;
        }

        return (
          <React.Fragment key={item.title}>
            <SideNavItem
              ref={itemRef}
              active={active}
              disabled={item.disabled}
              external={item.external}
              icon={item.icon}
              path={item.path}
              title={newTitle || item.title}
              hasChildren={!!item.children}
              onClick={() => {
                if (item.children) handleToggle(item.title);
              }}
              isSubUser={currentSession === "subUserSession"}
            />
            {item.children && (
              <Collapse in={openItems[item.title] || isActive(item)}>
                <Stack
                  component="ul"
                  spacing={0.5}
                  sx={{ listStyle: "none", pl: 2 }}
                >
                  {renderNavItems(item.children)}
                </Stack>
              </Collapse>
            )}
          </React.Fragment>
        );
      })}
    </Stack>
  );

  const subUserNavSteps =
    currentSession === "subUserSession"
      ? [0, 2, 5, 6]
      : [0, 2, 5, 7, 8, 21, 23, 25];

  const content = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box sx={{ p: { xs: 1.5, sm: 3 }, pb: { xs: 0.5, sm: 1 } }}>
        <Box
          sx={{
            textAlign: "center",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "transparent",
            cursor: "pointer",
            display: "flex",
            textDecoration: "none",
            borderRadius: { xs: "18px", sm: "27px" },
            // p: "12px",
          }}
        >
          <div>
            <img
              style={{
                width: "7rem",
                maxWidth: "100%",
                marginLeft: "auto",
                marginRight: "auto",
                marginTop: "-3px",
                marginBottom: "-13px",
              }}
              alt="Permit Rockstar Logo"
              src="/img/logos/logo_main.png"
            />
          </div>
        </Box>

        {currentUser?.role === "notary" ? (
          <Typography
            align="center"
            variant="h5"
            mt={{ xs: 0.5, sm: 1 }}
            sx={{ fontSize: { xs: "1rem", sm: "1.5rem" } }}
          >
            Notary
          </Typography>
        ) : (
          <>
            <Typography
              sx={{ fontStyle: "italic" }}
              align="center"
              variant="h6"
              mt={{ xs: 0.5, sm: 1 }}
              fontSize={{ xs: "0.95rem", sm: "1.25rem" }}
            >
              Beta Release
            </Typography>
            <Typography
              sx={{ fontStyle: "italic" }}
              align="center"
              variant="h6"
              mt={{ xs: 0.5, sm: 1 }}
              fontSize={{ xs: "0.95rem", sm: "0.8rem" }}
            >
              V1.0.7
            </Typography>
          </>
        )}
      </Box>
      <Divider sx={{ borderColor: "neutral.700" }} />
      <Box
        className={
          currentSession === "userSession"
            ? currentStep === 0
              ? "step1"
              : currentStep === 2
                ? "step3"
                : currentStep === 5
                  ? "step6"
                  : currentStep === 7
                    ? "step8"
                    : currentStep === 8
                      ? "step9"
                      : currentStep === 21
                        ? "step22"
                        : currentStep === 23
                          ? "step24"
                          : currentStep === 25
                            ? "step26"
                            : ""
            : currentSession === "subUserSession"
              ? currentStep === 0
                ? "step1"
                : currentStep === 2
                  ? "step3"
                  : currentStep === 5
                    ? "step8"
                    : currentStep === 6
                      ? "step9"
                      : // : currentStep === 8
                        // ? "step9"
                        // : currentStep === 21
                        // ? "step22"
                        // : currentStep === 23
                        // ? "step24"
                        // : currentStep === 25
                        // ? "step26"
                        ""
              : ""
        }
        component="nav"
        ref={scrollContainerRef}
        sx={
          subUserNavSteps.includes(currentStep) && isOpen
            ? scrollbarStyles
            : {
                overflowY: "auto",
                flexGrow: 1,
                px: 2,
                py: 3,
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }
        }
      >
        {currentUser?.role === "notary"
          ? renderNavItems(notaryItems)
          : renderNavItems(items)}
        {currentUser?.user?.role === "admin"
          ? renderNavItems(adminItems)
          : isSubAdmin
            ? renderNavItems(subAdminFilteredAdminItems)
            : currentUser?.user?.role === "privateProvider" &&
              renderNavItems(getPrivateProviderItems())}
      </Box>

      <SideNavAd
        handleMouseUp={handleMouseUp}
        handleMouseDown={handleMouseDown}
        handleMouseDownUp={handleMouseDownUp}
      />

      <Typography
        color="neutral.500"
        sx={{ textAlign: "center" }}
        variant="body2"
      >
        2023, All Rights Reserved
      </Typography>
    </Box>
  );

  // Respect global force hide request
  const [forceHideSideNav, setForceHideSideNav] = useState(false);
  useEffect(() => {
    const handleForceOn = () => setForceHideSideNav(true);
    const handleForceOff = () => setForceHideSideNav(false);
    window.addEventListener("forceHideSideNavOn", handleForceOn);
    window.addEventListener("forceHideSideNavOff", handleForceOff);
    return () => {
      window.removeEventListener("forceHideSideNavOn", handleForceOn);
      window.removeEventListener("forceHideSideNavOff", handleForceOff);
    };
  }, []);

  if (["/custom-documents/sign"].includes(pathname) && !forceHideSideNav) {
    if (lgUp && !open) {
      return (
        <Drawer
          anchor="left"
          open
          slotProps={{
            paper: {
              sx: {
                backgroundColor: "neutral.800",
                color: "common.white",
                width: props?.SIDE_NAV_WIDTH,
              },
            },
          }}
          variant="permanent"
        >
          {content}
        </Drawer>
      );
    } else if (lgUp && open) {
      return <></>;
    }
  } else if (lgUp && !forceHideSideNav) {
    return (
      <Drawer
        anchor="left"
        open
        PaperProps={{
          sx: {
            backgroundColor: "neutral.800",
            color: "common.white",
            width: props?.SIDE_NAV_WIDTH,
          },
        }}
        variant="permanent"
      >
        {content}
      </Drawer>
    );
  }

  if (forceHideSideNav) return <></>;

  return (
    <Drawer
      anchor="left"
      onClose={onClose}
      open={open}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: "neutral.800",
            color: "common.white",
            width: props?.SIDE_NAV_WIDTH,
          },
        },
      }}
      sx={{ zIndex: (theme) => theme.zIndex.appBar + 100 }}
      variant="temporary"
    >
      {content}
    </Drawer>
  );
};

export default React.memo(SideNav);
